import React, { useState } from "react";

import "./styles.css";

// ------------------
// Internal Constants
// ------------------

const defaultColor = "white";
const iconOnlyTranslateX = 235;
const iconOnlyWidth = 230;
const fullWidth = 700;
const nonIconWidth = fullWidth - iconOnlyWidth;
const defaultAnimationRate = 50;

// --------------
// Exported Props
// --------------

export interface ILogoProps {
  color?: string;
  backgroundColor?: string;
  hoverColor?: string;
  backgroundHoverColor?: string;
  iconOnly?: boolean;
  animationRate?: number;
}

// -------------------
// Exported Components
// -------------------

export const Logo: React.FC<ILogoProps> = ({
  color,
  backgroundColor,
  hoverColor,
  backgroundHoverColor,
  iconOnly,
  animationRate
}) => {
  color = color || defaultColor;
  animationRate = animationRate || defaultAnimationRate;

  const expectedTranslateX = iconOnly ? iconOnlyTranslateX : 0;
  const [hover, setHover] = useState(false);
  const [translateX, setTranslatex] = useState(expectedTranslateX);

  const width =
    iconOnlyWidth + nonIconWidth * (1 - translateX / iconOnlyTranslateX);

  if (translateX !== expectedTranslateX) {
    const delta = Math.floor(iconOnlyTranslateX / animationRate);
    const direction = translateX > expectedTranslateX ? -1 : 1;
    let newTranslateX = translateX + delta * direction;
    if (newTranslateX > iconOnlyTranslateX) {
      newTranslateX = iconOnlyTranslateX;
    } else if (newTranslateX < 0) {
      newTranslateX = 0;
    }
    requestAnimationFrame(() => {
      setTranslatex(newTranslateX);
    });
  }

  function onMouseEnter() {
    setHover(true);
  }

  function onMouseLeave() {
    setHover(false);
  }

  if (hover) {
    color = hoverColor || color;
  }

  return (
    <div
      className="logo"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      tabIndex={-1}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 706 236"
        version="1.1"
        fillRule="evenodd"
        clipRule="evenodd"
        strokeMiterlimit="2"
        pointerEvents="none"
      >
        <defs>
          <clipPath id="nonIconRect">
            <rect
              x={3 + iconOnlyWidth}
              y="3"
              width={width - iconOnlyWidth}
              height="230"
            />
          </clipPath>
        </defs>
        <g transform={`translate(${translateX},0)`}>
          <rect
            x="3"
            y="3"
            width={width}
            height="230"
            fillOpacity={
              (hover && backgroundHoverColor) || backgroundColor ? 1 : 0
            }
            stroke={color}
            strokeWidth="8px"
            fill={
              hover && backgroundHoverColor
                ? backgroundHoverColor
                : backgroundColor || undefined
            }
          />
          <path
            d="M233,0L233,236"
            fill="none"
            stroke={color}
            strokeWidth="8px"
          />
          <g>
            <path
              d="M39.633,168C37.326,168 35.643,167.505 34.586,166.514C33.529,165.523 33,163.946 33,161.784L33,74.216C33,72.054 33.529,70.477 34.586,69.486C35.643,68.495 37.326,68 39.633,68L103.941,68C106.249,68 107.931,68.495 108.988,69.486C110.046,70.477 110.574,72.054 110.574,74.216L110.574,90.297C110.574,92.459 110.046,94.036 108.988,95.027C107.931,96.018 106.249,96.514 103.941,96.514L68.615,96.514L68.615,107.865L92.262,107.865C94.569,107.865 96.251,108.36 97.309,109.351C98.366,110.342 98.895,111.919 98.895,114.081L98.895,130.297C98.895,132.459 98.366,134.036 97.309,135.027C96.251,136.018 94.569,136.514 92.262,136.514L68.615,136.514L68.615,161.784C68.615,163.946 68.086,165.523 67.029,166.514C65.971,167.505 64.289,168 61.982,168L39.633,168ZM61.982,166.649C63.809,166.649 65.13,166.266 65.947,165.5C66.764,164.734 67.173,163.495 67.173,161.784L67.173,135.162L92.262,135.162C94.088,135.162 95.41,134.779 96.227,134.014C97.044,133.248 97.453,132.009 97.453,130.297L97.453,114.081C97.453,112.369 97.044,111.131 96.227,110.365C95.41,109.599 94.088,109.216 92.262,109.216L67.173,109.216L67.173,95.162L103.941,95.162C105.768,95.162 107.09,94.779 107.907,94.014C108.724,93.248 109.132,92.009 109.132,90.297L109.132,74.216C109.132,72.505 108.724,71.266 107.907,70.5C107.09,69.734 105.768,69.351 103.941,69.351L39.633,69.351C37.806,69.351 36.485,69.734 35.668,70.5C34.85,71.266 34.442,72.505 34.442,74.216L34.442,161.784C34.442,163.495 34.85,164.734 35.668,165.5C36.485,166.266 37.806,166.649 39.633,166.649L61.982,166.649ZM50.159,154.486L50.159,81.514L96.155,81.514L96.155,82.865L51.601,82.865L51.601,121.514L84.476,121.514L84.476,122.865L51.601,122.865L51.601,154.486L50.159,154.486Z"
              fill="none"
              stroke={color}
              strokeWidth="4px"
              strokeLinejoin="round"
            />
            <path
              d="M203,142.865C203,150.973 200.212,157.189 194.637,161.514C189.062,165.838 181.42,168 171.711,168L126.147,168C123.84,168 122.157,167.505 121.1,166.514C120.043,165.523 119.514,163.946 119.514,161.784L119.514,145.973C119.514,143.811 120.043,142.234 121.1,141.243C122.157,140.252 123.84,139.757 126.147,139.757L164.934,139.757C165.703,139.757 166.28,139.486 166.664,138.946C167.049,138.405 167.241,137.82 167.241,137.189C167.241,136.198 167.049,135.432 166.664,134.892C166.28,134.351 165.655,134.036 164.79,133.946L143.161,131.108C135.952,130.117 130.208,127.887 125.93,124.419C121.653,120.95 119.514,115.252 119.514,107.324L119.514,93.135C119.514,85.027 122.278,78.811 127.805,74.486C133.332,70.162 140.998,68 150.803,68L191.176,68C193.483,68 195.166,68.495 196.223,69.486C197.28,70.477 197.809,72.054 197.809,74.216L197.809,90.297C197.809,92.459 197.28,94.036 196.223,95.027C195.166,96.018 193.483,96.514 191.176,96.514L157.58,96.514C156.811,96.514 156.21,96.784 155.778,97.324C155.345,97.865 155.129,98.495 155.129,99.216C155.129,100.027 155.345,100.703 155.778,101.243C156.21,101.784 156.859,102.099 157.724,102.189L179.209,104.892C186.418,105.883 192.186,108.113 196.511,111.581C200.837,115.05 203,120.748 203,128.676L203,142.865ZM171.711,166.649C180.939,166.649 188.221,164.599 193.556,160.5C198.891,156.401 201.558,150.523 201.558,142.865L201.558,128.676C201.558,121.108 199.491,115.703 195.358,112.459C191.224,109.216 185.793,107.144 179.064,106.243L157.58,103.541C156.331,103.45 155.369,103 154.696,102.189C154.023,101.378 153.687,100.387 153.687,99.216C153.687,98.045 154.023,97.077 154.696,96.311C155.369,95.545 156.331,95.162 157.58,95.162L191.176,95.162C193.003,95.162 194.325,94.779 195.142,94.014C195.959,93.248 196.367,92.009 196.367,90.297L196.367,74.216C196.367,72.505 195.959,71.266 195.142,70.5C194.325,69.734 193.003,69.351 191.176,69.351L150.803,69.351C141.479,69.351 134.173,71.401 128.886,75.5C123.599,79.599 120.956,85.477 120.956,93.135L120.956,107.324C120.956,114.892 122.999,120.297 127.084,123.541C131.169,126.784 136.576,128.856 143.305,129.757L164.934,132.595C166.183,132.685 167.121,133.09 167.746,133.811C168.37,134.532 168.683,135.658 168.683,137.189C168.683,138.27 168.37,139.194 167.746,139.959C167.121,140.725 166.183,141.108 164.934,141.108L126.147,141.108C124.32,141.108 122.999,141.491 122.182,142.257C121.364,143.023 120.956,144.261 120.956,145.973L120.956,161.784C120.956,163.495 121.364,164.734 122.182,165.5C122.999,166.266 124.32,166.649 126.147,166.649L171.711,166.649ZM183.39,81.514L183.39,82.865L151.38,82.865C147.343,82.865 144.122,83.901 141.719,85.973C139.316,88.045 138.115,90.658 138.115,93.811L138.115,104.892C138.115,108.045 138.787,110.252 140.133,111.514C141.479,112.775 143.257,113.586 145.468,113.946L175.892,119.757C178.776,120.297 181.155,121.468 183.03,123.27C184.904,125.072 185.841,128.315 185.841,133L185.841,140.838C185.841,144.802 184.399,148.068 181.516,150.635C178.632,153.203 174.739,154.486 169.836,154.486L133.933,154.486L133.933,153.135L169.836,153.135C174.258,153.135 177.791,151.986 180.434,149.689C183.078,147.392 184.399,144.441 184.399,140.838L184.399,133C184.399,128.766 183.606,125.86 182.02,124.284C180.434,122.707 178.295,121.649 175.604,121.108L145.18,115.297C142.681,114.847 140.638,113.923 139.052,112.527C137.466,111.131 136.673,108.586 136.673,104.892L136.673,93.811C136.673,90.207 138.018,87.257 140.71,84.959C143.401,82.662 146.958,81.514 151.38,81.514L183.39,81.514Z"
              fill="none"
              stroke={color}
              strokeWidth="4px"
              strokeLinejoin="round"
            />
          </g>

          <g clipPath="url(#nonIconRect)">
            <g transform={`translate(${-2 * translateX}, 0)`}>
              <path
                d="M301.877,75.736L288.641,75.736L288.641,84.819L301.41,84.819L301.41,90.181L288.641,90.181L288.641,108.175L282.908,108.175L282.908,70.375L301.877,70.375L301.877,75.736Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M306.675,89.104C306.675,83.783 308.635,79.213 312.555,75.394C316.459,71.574 321.15,69.665 326.628,69.665C332.041,69.665 336.683,71.591 340.554,75.443C344.441,79.294 346.385,83.921 346.385,89.324C346.385,94.759 344.433,99.37 340.529,103.156C336.609,106.959 331.877,108.861 326.333,108.861C321.429,108.861 317.025,107.171 313.121,103.793C308.824,100.055 306.675,95.159 306.675,89.104ZM312.457,89.177C312.457,93.355 313.867,96.791 316.689,99.484C319.493,102.177 322.733,103.523 326.407,103.523C330.393,103.523 333.755,102.152 336.494,99.41C339.233,96.636 340.603,93.257 340.603,89.275C340.603,85.244 339.25,81.865 336.543,79.139C333.854,76.397 330.524,75.026 326.555,75.026C322.602,75.026 319.264,76.397 316.541,79.139C313.818,81.849 312.457,85.195 312.457,89.177Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M366.461,92.066L378.222,108.175L371.21,108.175L360.36,92.702L359.326,92.702L359.326,108.175L353.594,108.175L353.594,70.375L360.31,70.375C365.33,70.375 368.954,71.313 371.185,73.19C373.645,75.279 374.876,78.038 374.876,81.465C374.876,84.142 374.105,86.443 372.563,88.369C371.021,90.295 368.987,91.527 366.461,92.066ZM359.326,87.733L361.147,87.733C366.576,87.733 369.291,85.668 369.291,81.539C369.291,77.67 366.65,75.736 361.368,75.736L359.326,75.736L359.326,87.733Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M384.446,70.375L394.337,95.64L404.67,68.539L414.561,95.64L425.067,70.375L431.291,70.375L414.315,110.574L404.547,83.791L394.362,110.599L378.222,70.375L384.446,70.375Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M455.107,98.994L438.845,98.994L434.613,108.175L428.437,108.175L447.234,68L465.367,108.175L459.093,108.175L455.107,98.994ZM452.77,93.633L447.136,80.78L441.231,93.633L452.77,93.633Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M482.983,92.066L494.744,108.175L487.732,108.175L476.881,92.702L475.848,92.702L475.848,108.175L470.116,108.175L470.116,70.375L476.832,70.375C481.851,70.375 485.476,71.313 487.707,73.19C490.167,75.279 491.397,78.038 491.397,81.465C491.397,84.142 490.627,86.443 489.085,88.369C487.543,90.295 485.509,91.527 482.983,92.066ZM475.848,87.733L477.669,87.733C483.098,87.733 485.813,85.668 485.813,81.539C485.813,77.67 483.172,75.736 477.89,75.736L475.848,75.736L475.848,87.733Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M500.452,108.175L500.452,70.375L508.423,70.375C512.245,70.375 515.263,70.75 517.477,71.501C519.855,72.235 522.012,73.484 523.948,75.247C527.868,78.805 529.828,83.481 529.828,89.275C529.828,95.085 527.786,99.786 523.702,103.377C521.652,105.172 519.503,106.42 517.256,107.122C515.156,107.824 512.179,108.175 508.325,108.175L500.452,108.175ZM506.184,102.813L508.768,102.813C511.343,102.813 513.483,102.544 515.189,102.006C516.895,101.434 518.437,100.528 519.814,99.288C522.636,96.726 524.046,93.388 524.046,89.275C524.046,85.129 522.652,81.767 519.864,79.188C517.354,76.887 513.655,75.736 508.768,75.736L506.184,75.736L506.184,102.813Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <g>
                <path
                  d="M566.782,108L566.782,101.155L584.595,90.5L566.782,79.786L566.782,73L590.383,87.583L590.383,93.446L566.782,108Z"
                  fill={color}
                  stroke={color}
                  strokeWidth="1px"
                  strokeLinejoin="round"
                />
                <path
                  d="M599.982,108L599.982,101.155L617.794,90.5L599.982,79.786L599.982,73L623.583,87.583L623.583,93.446L599.982,108Z"
                  fill={color}
                  stroke={color}
                  strokeWidth="1px"
                  strokeLinejoin="round"
                />
                <path
                  d="M633.182,108L633.182,101.155L650.994,90.5L633.182,79.786L633.182,73L656.782,87.583L656.782,93.446L633.182,108Z"
                  fill={color}
                  stroke={color}
                  strokeWidth="1px"
                  strokeLinejoin="round"
                />
              </g>
              <path
                d="M304.387,134.092L299.737,136.834C298.867,135.333 298.039,134.354 297.252,133.897C296.432,133.374 295.374,133.113 294.078,133.113C292.487,133.113 291.167,133.562 290.117,134.46C289.067,135.341 288.542,136.451 288.542,137.789C288.542,139.633 289.92,141.119 292.676,142.245L296.465,143.787C299.548,145.028 301.803,146.541 303.23,148.329C304.657,150.116 305.371,152.307 305.371,154.902C305.371,158.379 304.206,161.251 301.877,163.52C299.532,165.805 296.62,166.947 293.143,166.947C289.846,166.947 287.123,165.976 284.975,164.034C282.859,162.092 281.538,159.358 281.014,155.832L286.82,154.559C287.082,156.779 287.542,158.313 288.198,159.162C289.379,160.794 291.101,161.61 293.364,161.61C295.152,161.61 296.637,161.014 297.818,159.823C298.999,158.632 299.589,157.122 299.589,155.294C299.589,154.559 299.487,153.886 299.282,153.274C299.077,152.662 298.757,152.099 298.322,151.585C297.887,151.071 297.326,150.589 296.637,150.14C295.948,149.692 295.128,149.263 294.176,148.855L290.51,147.337C285.311,145.15 282.711,141.951 282.711,137.74C282.711,134.9 283.802,132.526 285.983,130.616C288.165,128.69 290.88,127.727 294.127,127.727C298.507,127.727 301.926,129.849 304.387,134.092Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M323.479,133.823L323.479,166.262L317.746,166.262L317.746,133.823L309.012,133.823L309.012,128.461L332.189,128.461L332.189,133.823L323.479,133.823Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M358.268,133.823L343.039,133.823L343.039,142.906L357.825,142.906L357.825,148.267L343.039,148.267L343.039,160.9L358.268,160.9L358.268,166.262L337.306,166.262L337.306,128.461L358.268,128.461L358.268,133.823Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M388.112,157.081L371.849,157.081L367.618,166.262L361.442,166.262L380.239,126.087L398.372,166.262L392.098,166.262L388.112,157.081ZM385.775,151.719L380.141,138.866L374.236,151.719L385.775,151.719Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M403.12,166.262L403.12,128.461L411.092,128.461C414.914,128.461 417.932,128.837 420.146,129.588C422.524,130.322 424.681,131.571 426.617,133.333C430.537,136.891 432.497,141.568 432.497,147.362C432.497,153.172 430.455,157.873 426.371,161.463C424.32,163.259 422.172,164.507 419.925,165.209C417.825,165.911 414.848,166.262 410.993,166.262L403.12,166.262ZM408.853,160.9L411.436,160.9C414.012,160.9 416.152,160.631 417.858,160.092C419.564,159.521 421.105,158.615 422.483,157.375C425.304,154.812 426.715,151.475 426.715,147.362C426.715,143.216 425.321,139.854 422.532,137.275C420.023,134.974 416.324,133.823 411.436,133.823L408.853,133.823L408.853,160.9Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M445.463,149.932L433.063,128.461L439.656,128.461L448.341,143.567L457.051,128.461L463.645,128.461L451.195,149.932L451.195,166.262L445.463,166.262L445.463,149.932Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M503.109,146.578L518.806,146.578L518.806,147.827C518.806,150.667 518.469,153.18 517.797,155.367C517.141,157.391 516.034,159.284 514.476,161.047C510.949,164.997 506.463,166.972 501.017,166.972C495.703,166.972 491.151,165.062 487.363,161.243C483.574,157.407 481.679,152.805 481.679,147.435C481.679,141.951 483.606,137.3 487.461,133.48C491.315,129.645 496.007,127.727 501.534,127.727C504.503,127.727 507.275,128.331 509.85,129.539C512.31,130.746 514.73,132.705 517.108,135.414L513.024,139.307C509.907,135.178 506.11,133.113 501.633,133.113C497.614,133.113 494.243,134.492 491.521,137.251C488.798,139.96 487.436,143.355 487.436,147.435C487.436,151.646 488.954,155.114 491.988,157.84C494.826,160.37 497.901,161.635 501.214,161.635C504.035,161.635 506.57,160.688 508.817,158.795C511.064,156.885 512.319,154.6 512.581,151.94L503.109,151.94L503.109,146.578Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M546.608,157.081L530.345,157.081L526.113,166.262L519.937,166.262L538.734,126.087L556.867,166.262L550.593,166.262L546.608,157.081ZM544.27,151.719L538.636,138.866L532.731,151.719L544.27,151.719Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M560.287,166.262L568.283,125.622L581.323,155L594.83,125.622L602.015,166.262L596.134,166.262L592.468,143.445L581.225,168L570.301,143.42L566.217,166.262L560.287,166.262Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M631.022,133.823L615.792,133.823L615.792,142.906L630.579,142.906L630.579,148.267L615.792,148.267L615.792,160.9L631.022,160.9L631.022,166.262L610.06,166.262L610.06,128.461L631.022,128.461L631.022,133.823Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
              <path
                d="M660.029,134.092L655.379,136.834C654.51,135.333 653.682,134.354 652.894,133.897C652.074,133.374 651.016,133.113 649.721,133.113C648.13,133.113 646.809,133.562 645.759,134.46C644.71,135.341 644.185,136.451 644.185,137.789C644.185,139.633 645.563,141.119 648.318,142.245L652.107,143.787C655.191,145.028 657.446,146.541 658.873,148.329C660.3,150.116 661.014,152.307 661.014,154.902C661.014,158.379 659.849,161.251 657.52,163.52C655.174,165.805 652.263,166.947 648.786,166.947C645.489,166.947 642.766,165.976 640.617,164.034C638.501,162.092 637.181,159.358 636.656,155.832L642.463,154.559C642.725,156.779 643.184,158.313 643.84,159.162C645.021,160.794 646.744,161.61 649.007,161.61C650.795,161.61 652.279,161.014 653.46,159.823C654.641,158.632 655.232,157.122 655.232,155.294C655.232,154.559 655.129,153.886 654.924,153.274C654.719,152.662 654.399,152.099 653.965,151.585C653.53,151.071 652.968,150.589 652.279,150.14C651.59,149.692 650.77,149.263 649.819,148.855L646.153,147.337C640.954,145.15 638.354,141.951 638.354,137.74C638.354,134.9 639.445,132.526 641.626,130.616C643.808,128.69 646.522,127.727 649.77,127.727C654.149,127.727 657.569,129.849 660.029,134.092Z"
                fill={color}
                stroke={color}
                strokeWidth="1px"
                strokeLinejoin="round"
              />
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
};
